import {
  LayoutDashboard, ListChecks, ClipboardList, Trello, ShieldCheck, Database,
  Grid3x3, Shield, BrainCircuit, Rocket, Scale, Building2, Gauge, Target,
  DollarSign, ShieldAlert, MonitorSmartphone, Network, Zap, ScrollText,
  Users, Plug,
} from "lucide-react";

// Column badge renderers supported: severity, status, trend, priority, progress, number, date
export const MODULES = {
  tasks: {
    entity: "tasks", labelKey: "tasks", icon: ClipboardList, screen: "tasks",
    fields: [
      { key: "title", label: "Task", primary: true },
      { key: "assignee", label: "Assignee" },
      { key: "domain", label: "Domain" },
      { key: "priority", labelKey: "priority", label: "Priority", badge: "severity" },
      { key: "dueDate", label: "Due", badge: "date" },
      { key: "status", labelKey: "status", label: "Status", badge: "status" },
    ],
    stats: [
      { label: "total", type: "count" },
      { label: "In Progress", type: "eq", key: "status", val: "In Progress", tone: "amber" },
      { label: "Blocked", type: "eq", key: "status", val: "Blocked", tone: "red" },
      { label: "Done", type: "eq", key: "status", val: "Done", tone: "emerald" },
    ],
  },
  "use-cases": {
    entity: "use_cases", labelKey: "useCases", icon: ListChecks, screen: "use_cases",
    fields: [
      { key: "title", labelKey: "title", label: "Title", primary: true },
      { key: "domain", label: "Domain" },
      { key: "owner", labelKey: "owner", label: "Owner" },
      { key: "priority", labelKey: "priority", label: "Priority", badge: "severity" },
      { key: "progress", label: "Progress", badge: "progress" },
      { key: "status", labelKey: "status", label: "Status", badge: "status" },
    ],
    stats: [
      { label: "total", type: "count" },
      { label: "Active", type: "eq", key: "status", val: "Active", tone: "emerald" },
      { label: "critical", type: "eq", key: "priority", val: "Critical", tone: "red" },
      { label: "Completed", type: "eq", key: "status", val: "Completed", tone: "blue" },
    ],
  },
  approvals: {
    entity: "approvals", labelKey: "approvals", icon: ShieldCheck, screen: "approvals",
    fields: [
      { key: "title", label: "Title", primary: true },
      { key: "requestedBy", label: "Requested By" },
      { key: "approver", label: "Approver" },
      { key: "priority", labelKey: "priority", label: "Priority", badge: "severity" },
      { key: "status", labelKey: "status", label: "Status", badge: "status" },
    ],
    stats: [
      { label: "total", type: "count" },
      { label: "Pending", type: "eq", key: "status", val: "Pending", tone: "amber" },
      { label: "Approved", type: "eq", key: "status", val: "Approved", tone: "emerald" },
      { label: "Rejected", type: "eq", key: "status", val: "Rejected", tone: "red" },
    ],
  },
  "cyber-gov": {
    entity: "cyber_gov", labelKey: "cyberGov", icon: Shield, screen: "cyber_gov",
    fields: [
      { key: "name", label: "Name", primary: true },
      { key: "maturity", label: "Maturity" },
      { key: "owner", labelKey: "owner", label: "Owner" },
      { key: "progress", label: "Progress", badge: "progress" },
      { key: "status", labelKey: "status", label: "Status", badge: "status" },
    ],
    stats: [{ label: "total", type: "count" }, { label: "Approved", type: "eq", key: "status", val: "Approved", tone: "emerald" }, { label: "Draft", type: "eq", key: "status", val: "Draft", tone: "amber" }],
  },
  "ai-readiness": {
    entity: "ai_readiness", labelKey: "aiReadiness", icon: BrainCircuit, screen: "ai_readiness",
    fields: [
      { key: "name", label: "Assessment", primary: true },
      { key: "score", label: "Score", badge: "number" },
      { key: "owner", labelKey: "owner", label: "Owner" },
      { key: "progress", label: "Progress", badge: "progress" },
      { key: "status", labelKey: "status", label: "Status", badge: "status" },
    ],
    stats: [{ label: "total", type: "count" }, { label: "Ready", type: "eq", key: "status", val: "Ready", tone: "emerald" }, { label: "Gap", type: "eq", key: "status", val: "Gap", tone: "red" }],
  },
  "ai-projects": {
    entity: "ai_projects", labelKey: "aiProjects", icon: Rocket, screen: "ai_projects",
    fields: [
      { key: "name", label: "Project", primary: true },
      { key: "owner", labelKey: "owner", label: "Owner" },
      { key: "priority", labelKey: "priority", label: "Priority", badge: "severity" },
      { key: "progress", label: "Progress", badge: "progress" },
      { key: "status", labelKey: "status", label: "Status", badge: "status" },
    ],
    stats: [{ label: "total", type: "count" }, { label: "Deployed", type: "eq", key: "status", val: "Deployed", tone: "emerald" }, { label: "Piloting", type: "eq", key: "status", val: "Piloting", tone: "amber" }],
  },
  regulatory: {
    entity: "regulatory", labelKey: "regulatory", icon: Scale, screen: "regulatory",
    fields: [
      { key: "name", label: "Control", primary: true },
      { key: "framework", label: "Framework" },
      { key: "owner", labelKey: "owner", label: "Owner" },
      { key: "progress", label: "Progress", badge: "progress" },
      { key: "status", labelKey: "status", label: "Status", badge: "status" },
    ],
    stats: [{ label: "total", type: "count" }, { label: "Compliant", type: "eq", key: "status", val: "Compliant", tone: "emerald" }, { label: "Non-Compliant", type: "eq", key: "status", val: "Non-Compliant", tone: "red" }],
  },
  "business-impact": {
    entity: "business_impact", labelKey: "businessImpact", icon: Building2, screen: "business_impact",
    fields: [
      { key: "name", label: "Process", primary: true },
      { key: "criticality", label: "Criticality", badge: "severity" },
      { key: "rto", label: "RTO" },
      { key: "rpo", label: "RPO" },
      { key: "status", labelKey: "status", label: "Status", badge: "status" },
    ],
    stats: [{ label: "total", type: "count" }, { label: "critical", type: "eq", key: "criticality", val: "Critical", tone: "red" }, { label: "Assessed", type: "eq", key: "status", val: "Assessed", tone: "emerald" }],
  },
  kri: {
    entity: "kri", labelKey: "kri", icon: Gauge, screen: "kri",
    fields: [
      { key: "name", label: "Indicator", primary: true },
      { key: "value", label: "Value", badge: "number" },
      { key: "threshold", label: "Threshold", badge: "number" },
      { key: "trend", label: "Trend", badge: "trend" },
      { key: "status", labelKey: "status", label: "Status", badge: "status" },
    ],
    stats: [{ label: "total", type: "count" }, { label: "Breach", type: "eq", key: "status", val: "Breach", tone: "red" }, { label: "Warning", type: "eq", key: "status", val: "Warning", tone: "amber" }],
  },
  kpi: {
    entity: "kpi", labelKey: "kpi", icon: Target, screen: "kpi",
    fields: [
      { key: "name", label: "Metric", primary: true },
      { key: "value", label: "Value", badge: "number" },
      { key: "target", label: "Target", badge: "number" },
      { key: "trend", label: "Trend", badge: "trend" },
      { key: "status", labelKey: "status", label: "Status", badge: "status" },
    ],
    stats: [{ label: "total", type: "count" }, { label: "On Track", type: "eq", key: "status", val: "On Track", tone: "emerald" }, { label: "Behind", type: "eq", key: "status", val: "Behind", tone: "red" }],
  },
  "business-value": {
    entity: "business_value", labelKey: "businessValue", icon: DollarSign, screen: "business_value",
    fields: [
      { key: "name", label: "Value Driver", primary: true },
      { key: "value", label: "Value" },
      { key: "owner", labelKey: "owner", label: "Owner" },
      { key: "progress", label: "Progress", badge: "progress" },
      { key: "status", labelKey: "status", label: "Status", badge: "status" },
    ],
    stats: [{ label: "total", type: "count" }, { label: "Realized", type: "eq", key: "status", val: "Realized", tone: "emerald" }, { label: "Projected", type: "eq", key: "status", val: "Projected", tone: "amber" }],
  },
  soc: {
    entity: "soc", labelKey: "soc", icon: ShieldAlert, screen: "soc",
    fields: [
      { key: "name", label: "Alert", primary: true },
      { key: "type", label: "Type" },
      { key: "source", label: "Source" },
      { key: "severity", labelKey: "severity", label: "Severity", badge: "severity" },
      { key: "status", labelKey: "status", label: "Status", badge: "status" },
    ],
    stats: [{ label: "total", type: "count" }, { label: "critical", type: "eq", key: "severity", val: "Critical", tone: "red" }, { label: "New", type: "eq", key: "status", val: "New", tone: "amber" }],
  },
  "app-perf": {
    entity: "app_perf", labelKey: "appPerf", icon: MonitorSmartphone, screen: "app_perf",
    fields: [
      { key: "name", label: "Application", primary: true },
      { key: "uptime", label: "Uptime %", badge: "number" },
      { key: "latency", label: "Latency ms", badge: "number" },
      { key: "errorRate", label: "Error %", badge: "number" },
      { key: "status", labelKey: "status", label: "Status", badge: "status" },
    ],
    stats: [{ label: "total", type: "count" }, { label: "Healthy", type: "eq", key: "status", val: "Healthy", tone: "emerald" }, { label: "Down", type: "eq", key: "status", val: "Down", tone: "red" }],
  },
  "net-perf": {
    entity: "network_perf", labelKey: "netPerf", icon: Network, screen: "network_perf",
    fields: [
      { key: "name", label: "Device", primary: true },
      { key: "throughput", label: "Throughput" },
      { key: "packetLoss", label: "Loss %", badge: "number" },
      { key: "latency", label: "Latency ms", badge: "number" },
      { key: "status", labelKey: "status", label: "Status", badge: "status" },
    ],
    stats: [{ label: "total", type: "count" }, { label: "Optimal", type: "eq", key: "status", val: "Optimal", tone: "emerald" }, { label: "Offline", type: "eq", key: "status", val: "Offline", tone: "red" }],
  },
  soar: {
    entity: "soar", labelKey: "soar", icon: Zap, screen: "soar",
    fields: [
      { key: "name", label: "Playbook", primary: true },
      { key: "trigger", label: "Trigger" },
      { key: "runs", label: "Runs", badge: "number" },
      { key: "successRate", label: "Success %", badge: "number" },
      { key: "status", labelKey: "status", label: "Status", badge: "status" },
    ],
    stats: [{ label: "total", type: "count" }, { label: "Active", type: "eq", key: "status", val: "Active", tone: "emerald" }, { label: "Paused", type: "eq", key: "status", val: "Paused", tone: "amber" }],
  },
  incidents: {
    entity: "incidents", labelKey: "openIncidents", icon: ShieldAlert, screen: "incidents",
    fields: [
      { key: "title", label: "Incident", primary: true },
      { key: "category", label: "Category" },
      { key: "reportedBy", label: "Reported By" },
      { key: "severity", labelKey: "severity", label: "Severity", badge: "severity" },
      { key: "status", labelKey: "status", label: "Status", badge: "status" },
    ],
    stats: [{ label: "total", type: "count" }, { label: "open", type: "eq", key: "status", val: "Open", tone: "red" }, { label: "critical", type: "eq", key: "severity", val: "Critical", tone: "red" }],
  },
  integrations: {
    entity: "integrations", labelKey: "integrations", icon: Plug, screen: "integrations",
    fields: [
      { key: "name", label: "Tool", primary: true },
      { key: "category", label: "Category" },
      { key: "owner", labelKey: "owner", label: "Owner" },
      { key: "status", labelKey: "status", label: "Status", badge: "status" },
    ],
    stats: [{ label: "total", type: "count" }, { label: "Connected", type: "eq", key: "status", val: "Connected", tone: "emerald" }, { label: "Error", type: "eq", key: "status", val: "Error", tone: "red" }],
  },
  users: {
    entity: "users", labelKey: "userManager", icon: Users, screen: "users",
    fields: [
      { key: "name", label: "Name", primary: true },
      { key: "email", labelKey: "email", label: "Email" },
      { key: "role", labelKey: "role", label: "Role" },
      { key: "status", labelKey: "status", label: "Status", badge: "status" },
    ],
    stats: [{ label: "total", type: "count" }, { label: "Active", type: "eq", key: "status", val: "Active", tone: "emerald" }],
  },
  "audit-log": {
    entity: "audit_log", labelKey: "auditLog", icon: ScrollText, screen: "audit_log", readOnly: true,
    fields: [
      { key: "action", label: "Action", primary: true },
      { key: "entity", label: "Entity" },
      { key: "detail", label: "Detail" },
      { key: "user", label: "User" },
      { key: "timestamp", label: "Time", badge: "date" },
    ],
    stats: [{ label: "total", type: "count" }],
  },
};

// Sidebar order incl. non-generic pages
export const NAV = [
  { to: "/app/dashboard", labelKey: "dashboard", icon: LayoutDashboard },
  { to: "/app/use-cases", labelKey: "useCases", icon: ListChecks },
  { to: "/app/tasks", labelKey: "tasks", icon: ClipboardList },
  { to: "/app/kanban", labelKey: "kanban", icon: Trello },
  { to: "/app/approvals", labelKey: "approvals", icon: ShieldCheck },
  { to: "/app/registers", labelKey: "registers", icon: Database },
  { to: "/app/risk-heat-map", labelKey: "riskHeatMap", icon: Grid3x3 },
  { to: "/app/cyber-gov", labelKey: "cyberGov", icon: Shield },
  { to: "/app/ai-readiness", labelKey: "aiReadiness", icon: BrainCircuit },
  { to: "/app/ai-projects", labelKey: "aiProjects", icon: Rocket },
  { to: "/app/regulatory", labelKey: "regulatory", icon: Scale },
  { to: "/app/business-impact", labelKey: "businessImpact", icon: Building2 },
  { to: "/app/kri", labelKey: "kri", icon: Gauge },
  { to: "/app/kpi", labelKey: "kpi", icon: Target },
  { to: "/app/business-value", labelKey: "businessValue", icon: DollarSign },
  { to: "/app/soc", labelKey: "soc", icon: ShieldAlert },
  { to: "/app/app-perf", labelKey: "appPerf", icon: MonitorSmartphone },
  { to: "/app/net-perf", labelKey: "netPerf", icon: Network },
  { to: "/app/soar", labelKey: "soar", icon: Zap },
  { to: "/app/audit-log", labelKey: "auditLog", icon: ScrollText },
  { to: "/app/users", labelKey: "userManager", icon: Users },
  { to: "/app/integrations", labelKey: "integrations", icon: Plug },
];
